import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage1.css";
import { fireGameAlert } from "./gameAlert";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 1;
/** Correct hits needed to clear the stage (old/stage1.php: rightanser == 5). */
const MIN_RIGHT_TO_ADVANCE = 5;
/** old/stage1.php: points = points + 1 per correct click. */
const POINTS_PER_RIGHT = 1;

const SCENE_URL = `${process.env.PUBLIC_URL}/assets/game/stage1-scene.svg`;
const SCENE_URL_MOBILE = `${process.env.PUBLIC_URL}/assets/game/stage1_Mob.svg`;
const STAGE1_BG = `${process.env.PUBLIC_URL}/assets/game/stage1-bg.jpg`;
const STAGE1_BG_MOBILE = `${process.env.PUBLIC_URL}/assets/game/stage1_mob.png`;
const POPUP_BG = `${process.env.PUBLIC_URL}/assets/game/popup-bg.png`;

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

const RIGHT_WORDS = ["Influence", "Leadership", "Empowerment", "Mastery", "Authority"];
const WRONG_WORDS = ["Domination", "Intimidation", "Manipulation", "Exploitation"];
const ALL_WORDS = [...RIGHT_WORDS, ...WRONG_WORDS];

const RULES_TEXT =
  "The first 3 days are dedicated to Goddess Durga, who represents the power to destroy evil forces. " +
  "These days signify the initial step in overcoming evil - recognizing it and summoning the inner strength to confront it";

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

// Desktop scene marks each word's clickable shapes with a `content="Word"`
// attribute directly on those shapes. The mobile scene instead wraps each
// word's paths in a single `<g id="Word">` group, so the group itself has to
// be resolved to its descendant <path> nodes for recoloring.
function getWordNodes(root, word) {
  if (!root) return [];
  const byContent = root.querySelectorAll(`[content="${word}"]`);
  if (byContent.length) return Array.from(byContent);
  const group = root.querySelector(`[id="${word}"]`);
  if (!group) return [];
  const paths = group.querySelectorAll("path");
  return paths.length ? Array.from(paths) : [group];
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage1() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  /** Points earned in stage 1 this session (resumes from DB stage1 column). */
  const [stage1Points, setStage1Points] = useState(0);
  /** HUD total = other stages + stage1Points. */
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [sceneReady, setSceneReady] = useState(false);
  const [showRules, setShowRules] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isMobilePortrait, setIsMobilePortrait] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(MOBILE_PORTRAIT_MQ).matches
      : false
  );

  const sceneRef = useRef(null);
  const answeredRef = useRef(new Set());
  const rightCountRef = useRef(0);
  /** Mirrors stage1Points synchronously — finishStage can be called in the
   * same tick as the 5th point's setStage1Points, before React flushes that
   * state update, so it must read this ref instead of the stale state value. */
  const stage1PointsRef = useRef(0);
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };

    const applyBackground = () => {
      const mobilePortrait = window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
      setIsMobilePortrait(mobilePortrait);
      body.style.backgroundImage = `url("${mobilePortrait ? STAGE1_BG_MOBILE : STAGE1_BG}")`;
      body.style.backgroundSize = mobilePortrait ? "cover" : "100% 100%";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundPosition = "center center";
      body.style.backgroundAttachment = "fixed";
    };

    html.classList.add("stage1-lock");
    body.classList.remove("common-bg");
    body.classList.add("stage1-superpowers-page");
    applyBackground();
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);
    return () => {
      html.classList.remove("stage1-lock");
      body.classList.remove("stage1-superpowers-page");
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  // Fetch the illustrated scene and inject it so its internal elements
  // (grouped by class per word: Influence, Domination, ...) are real DOM
  // nodes we can query/recolor — an <img> can't be targeted like that.
  // Re-fetches the portrait-cropped scene when crossing the mobile breakpoint,
  // reapplying any already-answered words' colors onto the freshly injected markup.
  // Gated on dbReady: until then the component renders the "Loading game…"
  // placeholder instead of this JSX, so sceneRef.current would still be null.
  useEffect(() => {
    if (!dbReady) return undefined;
    let cancelled = false;
    const url = isMobilePortrait ? SCENE_URL_MOBILE : SCENE_URL;
    setSceneReady(false);
    fetch(url)
      .then((res) => res.text())
      .then((svgText) => {
        if (cancelled || !sceneRef.current) return;
        sceneRef.current.innerHTML = svgText;
        // Default preserveAspectRatio ("meet") letterboxes/pillarboxes whenever the
        // scene's own aspect ratio doesn't match the viewport — "slice" crops
        // instead, so the illustration fills the container edge to edge.
        const svgEl = sceneRef.current.querySelector("svg");
        if (svgEl) svgEl.setAttribute("preserveAspectRatio", "xMidYMid slice");
        // Desktop's shapes get the pointer cursor via the [content] CSS selector;
        // the mobile scene's word wrappers only have an id, so tag them directly.
        ALL_WORDS.forEach((word) => {
          const el = sceneRef.current.querySelector(`[id="${word}"]`);
          if (el) el.classList.add("stage1-word-hit");
        });
        answeredRef.current.forEach((word) => {
          const isRight = RIGHT_WORDS.includes(word);
          const nodes = getWordNodes(sceneRef.current, word);
          nodes.forEach((node) => {
            node.style.fill = isRight ? "green" : "red";
          });
        });
        setSceneReady(true);
      })
      .catch((err) => console.error("Stage1 scene load:", err));
    return () => {
      cancelled = true;
    };
  }, [dbReady, isMobilePortrait]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;

    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage1Points);
  }, [stage1Points]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage1 = Number(st.stage1) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage1);
          stage1PointsRef.current = dbStage1;
          setStage1Points(dbStage1);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      if (!skip && rightCountRef.current < MIN_RIGHT_TO_ADVANCE) return;
      submittedRef.current = true;
      setSubmitting(true);

      // old/stage1.php's send_skip() always submits 0 points on skip.
      // Reads the ref, not the stage1Points state — finishStage can be invoked
      // in the same tick as the 5th correct click's setStage1Points, before
      // React has flushed that update, so the state value would still be stale.
      const stage1Score = skip ? 0 : stage1PointsRef.current;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + stage1Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: stage1Score,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1200));
      navigate("/stage2");
    },
    [submitting, storedUser, isDemoBypass, backendBase, adminToken, stageRowId, navigate]
  );

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const handleSceneClick = useCallback(
    (e) => {
      if (submitting || submittedRef.current) return;
      const target = e.target.closest ? e.target.closest("[content], [id]") : null;
      if (!target) return;
      const word = target.getAttribute("content") || target.getAttribute("id");
      if (!word || !ALL_WORDS.includes(word)) return;
      if (answeredRef.current.has(word)) return;

      answeredRef.current.add(word);

      const isRight = RIGHT_WORDS.includes(word);
      const color = isRight ? "green" : "red";
      const nodes = getWordNodes(sceneRef.current, word);
      nodes.forEach((node) => {
        node.style.fill = color;
      });

      const isFirstAnswer = answeredRef.current.size === 1;

      if (isRight) {
        rightCountRef.current += 1;
        stage1PointsRef.current += POINTS_PER_RIGHT;
        setStage1Points(stage1PointsRef.current);
      }

      if (isDemoBypass && isFirstAnswer && !demoRedirectTriggeredRef.current) {
        demoRedirectTriggeredRef.current = true;
        setTimeout(() => {
          void fireGameAlert({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign("https://extramileplay.com/plans");
          });
        }, 400);
        return;
      }

      if (isRight && rightCountRef.current >= MIN_RIGHT_TO_ADVANCE) {
        finishStage(false);
      }
    },
    [submitting, isDemoBypass, finishStage]
  );

  if (!dbReady) {
    return <div className="stage1-loading">Loading game…</div>;
  }

  const clock = formatSecondsToClock(elapsedSec);

  return (
    <div className="stage1-page">
      <div className="stage1-scene-wrap" ref={sceneRef} onClick={handleSceneClick} />
      {!sceneReady ? <div className="stage1-scene-loading">Loading scene…</div> : null}

      <div className="stage1-hud-row">
        <div className="stage1-hud-col stage1-hud-col--left">
          <div className="stage1-timericon" aria-label={`Time ${clock}`}>
            Time: {clock}
          </div>
        </div>
        <div className="stage1-hud-col stage1-hud-col--center">
          <div className="stage1-heading-box">
            <h1 className="stage1-head-title">
              In this stage, highlight superpowers that help us in overcoming obstacles coming our way
            </h1>
          </div>
        </div>
        <div className="stage1-hud-col stage1-hud-col--right">
          <div className="stage1-point-box">
            <h4>Score:</h4> <span>{displayTotalScore}</span>
          </div>
          <button
            type="button"
            className="stage1-timericon stage1-skip"
            disabled={submitting}
            onClick={handleSkip}
          >
            Skip
          </button>
        </div>
      </div>

      {showRules ? (
        <div className="stage1-rules-overlay" role="dialog" aria-modal="true">
          <div
            className="stage1-rules-card"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,.35), rgba(0,0,0,.35)), url("${POPUP_BG}")`,
            }}
          >
            <button
              type="button"
              className="stage1-rules-close"
              aria-label="Close"
              onClick={() => setShowRules(false)}
            >
              &times;
            </button>
            <p>{RULES_TEXT}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
